import {
  Component,
  OnInit,
  OnDestroy,
  Renderer2,
  Inject,
  PLATFORM_ID,
  AfterViewInit,
} from '@angular/core';

import {
  DOCUMENT,
  isPlatformBrowser,
  ViewportScroller,
} from '@angular/common';

import {
  Router,
  NavigationEnd,
  NavigationStart,
} from '@angular/router';

import { filter, Subscription } from 'rxjs';

import { LanguageServiceService } from './services/language-service.service';
import { DeviceDetectorService } from './services/device-detector.service';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'ElShahat IN';

  showLoading = true;

  isMobile = false;
  isIos = false;
  isRtl = false;
  isAr = false;

  welcome = false;
  showPopup = false;

  showInstallPopup = false;

  canInstallPWA = false;
  isInstallingPWA = false;

  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  private arabicStyleId = 'arabic-style';
  private subscriptions = new Subscription();

  private viewReady = false;
  private firstDirectionEmit = true;
  private loadingFallbackTimer?: ReturnType<typeof setTimeout>;

  private beforeInstallPromptHandler = (event: Event): void => {
    this.onBeforeInstallPrompt(event);
  };

  private appInstalledHandler = (): void => {
    this.onAppInstalled();
  };

  constructor(
    private _languageService: LanguageServiceService,
    private deviceService: DeviceDetectorService,
    private renderer: Renderer2,
    private router: Router,
    private viewportScroller: ViewportScroller,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    if (this.isBrowser()) {
      this.detectDevice();

      this.welcome = localStorage.getItem('welcome') === 'true';
      this.isAr = localStorage.getItem('language') === 'ar';
      this.isRtl = this.isAr;

      this.checkSession();

      window.addEventListener(
        'beforeinstallprompt',
        this.beforeInstallPromptHandler
      );

      window.addEventListener('appinstalled', this.appInstalledHandler);

      /**
       * iOS doesn't support beforeinstallprompt.
       * Show instructions only.
       */
      if (
        this.isMobile &&
        this.isIos &&
        !this.isAppInstalled() &&
        !this.isInstallPopupClosed()
      ) {
        setTimeout(() => {
          this.showInstallPopup = true;
        }, 1200);
      }

      this.subscriptions.add(
        this.router.events
          .pipe(
            filter(
              (event): event is NavigationStart =>
                event instanceof NavigationStart
            )
          )
          .subscribe(() => {
            this.playLoading();
          })
      );

      this.subscriptions.add(
        this.router.events
          .pipe(
            filter(
              (event): event is NavigationEnd =>
                event instanceof NavigationEnd
            )
          )
          .subscribe(() => {
            setTimeout(() => {
              this.viewportScroller.scrollToPosition([0, 0]);
            }, 0);
          })
      );
    }

    this.subscriptions.add(
      this._languageService.currentDirection$.subscribe((direction) => {
        this.document.documentElement.dir = direction;

        this.isRtl = direction === 'rtl';
        this.isAr = direction === 'rtl';

        if (direction === 'rtl') {
          this.addArabicStylesheet();
        } else {
          this.removeArabicStylesheet();
        }

        if (this.isBrowser()) {
          if (this.firstDirectionEmit) {
            this.firstDirectionEmit = false;
          } else if (this.viewReady) {
            this.playLoading();
          }
        }
      })
    );
  }

  ngAfterViewInit(): void {
    if (this.isBrowser()) {
      this.viewReady = true;

      setTimeout(() => {
        this.playLoading();
      }, 0);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();

    if (this.loadingFallbackTimer) {
      clearTimeout(this.loadingFallbackTimer);
    }

    if (this.isBrowser()) {
      window.removeEventListener(
        'beforeinstallprompt',
        this.beforeInstallPromptHandler
      );

      window.removeEventListener('appinstalled', this.appInstalledHandler);
    }
  }

  playLoading(): void {
    if (!this.isBrowser()) return;

    this.showLoading = true;

    if (this.loadingFallbackTimer) {
      clearTimeout(this.loadingFallbackTimer);
    }

    this.loadingFallbackTimer = setTimeout(() => this.hideLoading(), 1200);
  }

  hideLoading(): void {
    this.showLoading = false;

    if (this.loadingFallbackTimer) {
      clearTimeout(this.loadingFallbackTimer);
      this.loadingFallbackTimer = undefined;
    }
  }

  /**
   * Android / Chrome PWA install event
   */
  private onBeforeInstallPrompt(event: Event): void {
    if (!this.isBrowser()) return;

    event.preventDefault();

    this.deferredPrompt = event as BeforeInstallPromptEvent;
    this.canInstallPWA = true;

    if (
      this.isMobile &&
      !this.isIos &&
      !this.isAppInstalled() &&
      !this.isInstallPopupClosed()
    ) {
      this.showInstallPopup = true;
    }

    console.log('PWA install prompt is ready');
  }

  /**
   * App installed successfully
   */
  private onAppInstalled(): void {
    if (!this.isBrowser()) return;

    this.showInstallPopup = false;
    this.canInstallPWA = false;
    this.isInstallingPWA = false;
    this.deferredPrompt = null;

    localStorage.setItem('atw-pwa-installed', 'true');

    console.log('PWA installed successfully');
  }

  async installPWA(): Promise<void> {
    if (!this.isBrowser()) return;

    if (this.isIos) {
      return;
    }

    if (!this.deferredPrompt) {
      console.warn('PWA install prompt is not available');
      this.canInstallPWA = false;
      return;
    }

    try {
      this.isInstallingPWA = true;

      await this.deferredPrompt.prompt();

      const choiceResult = await this.deferredPrompt.userChoice;

      console.log('PWA install choice:', choiceResult.outcome);

      this.showInstallPopup = false;
      this.canInstallPWA = false;
      this.deferredPrompt = null;

      if (choiceResult.outcome === 'accepted') {
        localStorage.setItem('atw-pwa-installed', 'true');
      } else {
        sessionStorage.setItem('atw-pwa-popup-closed', 'true');
      }
    } catch (error) {
      console.error('PWA install failed:', error);
    } finally {
      this.isInstallingPWA = false;
    }
  }

  closeInstallPopup(): void {
    this.showInstallPopup = false;

    if (this.isBrowser()) {
      sessionStorage.setItem('atw-pwa-popup-closed', 'true');
    }
  }

  changeLanguage(language: string): void {
    this.playLoading();
    this._languageService.changeLanguage(language);
  }

  openPopup(): void {
    this.showPopup = true;
  }

  closePopup(): void {
    this.showPopup = false;
  }

  setCookie(c_name: string, value: string, exdays: number | null): void {
    if (!this.isBrowser()) return;

    const exdate = new Date();

    if (exdays !== null) {
      exdate.setDate(exdate.getDate() + exdays);
    }

    const c_value =
      encodeURIComponent(value) +
      (exdays === null ? '' : '; expires=' + exdate.toUTCString());

    this.document.cookie = c_name + '=' + c_value + '; path=/';
  }

  getCookie(c_name: string): string {
    if (!this.isBrowser()) return '';

    const name = c_name + '=';
    const decodedCookie = decodeURIComponent(this.document.cookie);
    const ca = decodedCookie.split(';');

    for (let c of ca) {
      c = c.trim();

      if (c.indexOf(name) === 0) {
        return c.substring(name.length);
      }
    }

    return '';
  }

  checkSession(): void {
    if (!this.isBrowser()) return;

    const c = this.getCookie('visited');

    if (c !== 'yes') {
      this.showPopup = true;
      this.setCookie('visited', 'yes', null);
    }
  }

  private detectDevice(): void {
    if (!this.isBrowser()) return;

    const userAgent = window.navigator.userAgent.toLowerCase();

    this.isMobile =
      this.deviceService.isMobileDevice() ||
      /android|iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
      window.innerWidth <= 760;

    this.isIos = /iphone|ipad|ipod/.test(userAgent);
  }

  private isAppInstalled(): boolean {
    if (!this.isBrowser()) return false;

    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    return standalone || localStorage.getItem('atw-pwa-installed') === 'true';
  }

  private isInstallPopupClosed(): boolean {
    if (!this.isBrowser()) return true;

    return sessionStorage.getItem('atw-pwa-popup-closed') === 'true';
  }

  private addArabicStylesheet(): void {
    if (!this.document.getElementById(this.arabicStyleId)) {
      const linkEl = this.renderer.createElement('link');

      linkEl.id = this.arabicStyleId;
      linkEl.rel = 'stylesheet';
      linkEl.href = '/assets/styles-ar.css';

      this.renderer.appendChild(this.document.head, linkEl);
    }
  }

  private removeArabicStylesheet(): void {
    const existingLink = this.document.getElementById(this.arabicStyleId);

    if (existingLink) {
      this.renderer.removeChild(this.document.head, existingLink);
    }
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }
}